import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Cloud, FolderOpen, Loader2, Server } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Panel } from "../admin/fields";

const Mode = ({ value, current, onPick, icon: Icon, title, sub }) => (
  <button type="button" onClick={() => onPick(value)} data-testid={`storage-mode-${value}`}
    className={`flex flex-1 items-start gap-3 rounded-2xl border p-4 text-left transition-colors ${current === value ? "border-brand bg-brand-light/40 ring-1 ring-brand" : "border-ink/10 hover:border-brand/40"}`}>
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
    <span><span className="block font-medium text-ink">{title}</span><span className="text-sm text-ink/60">{sub}</span></span>
  </button>
);

export const StoragePanel = () => {
  const { data } = useQuery({ queryKey: ["platform-storage"], queryFn: () => api.get("/platform/storage").then((r) => r.data) });
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (data) setForm(data);
  }, [data]);
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save") {
        const saved = (await api.put("/platform/storage", form)).data;
        qc.setQueryData(["platform-storage"], saved);
        toast.success(saved.mode === "local" ? `Images will be saved in ${saved.folder}` : "Images will use cloud storage");
      } else {
        toast.success(`The app can write to ${(await api.post("/platform/storage/test")).data.folder}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };
  if (!form) return null;
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  return (
    <Panel title="Image storage" hint="Where uploaded logos and photos are kept. On your own server (VPS), choose a server folder. Images already uploaded stay where they were saved.">
      <p className="mb-4 text-xs text-ink/60" data-testid="storage-current">Now using: <b>{data.mode === "local" ? `server folder ${data.folder}` : "Emergent cloud storage"}</b></p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Mode value="local" current={form.mode} onPick={(mode) => set({ mode })} icon={Server} title="Folder on this server" sub="Best for your VPS" />
        <Mode value="emergent" current={form.mode} onPick={(mode) => set({ mode })} icon={Cloud} title="Emergent cloud storage" sub="Only works when hosted on Emergent" />
      </div>
      {form.mode === "local" && (
        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <label className="field"><span className="field-label">Folder path on the server</span>
            <input className="field-input font-mono" value={form.folder} onChange={(e) => set({ folder: e.target.value })} placeholder="/var/www/hostech/uploads" data-testid="storage-folder" />
            <span className="mt-1 text-xs text-ink/50">The app creates it if missing. The app's user needs write access.</span>
          </label>
          <label className="field"><span className="field-label">Public web address of that folder (optional)</span>
            <input className="field-input font-mono" value={form.public_url} onChange={(e) => set({ public_url: e.target.value })} placeholder="https://book.hostechsolutions.com/uploads" data-testid="storage-public-url" />
            <span className="mt-1 text-xs text-ink/50">Leave empty and the app serves images itself. Fill it only if your web server already publishes this folder.</span>
          </label>
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        <button type="button" className="btn-primary" disabled={!!busy} onClick={() => run("save")} data-testid="storage-save">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderOpen className="h-4 w-4" />}Save
        </button>
        {data.mode === "local" && <button type="button" className="btn-outline" disabled={!!busy} onClick={() => run("test")} data-testid="storage-test">
          {busy === "test" && <Loader2 className="h-4 w-4 animate-spin" />}Test folder
        </button>}
      </div>
    </Panel>
  );
};
